import React from "react";
import { Button, Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";

import { AUTOPILOT_COLORS } from "components/autopilot/styles";

import "./styles.css";

// Peças comuns das telas de Cadastros Gerais no padrão do Hub (PO1-2723),
// revistas na sprint 638: helpers de texto/status, estados da lista,
// confirmação e selo de status por token (sem cor cravada).

// Cadastros globais (CEP, Modais, Tipos de serviço, Taxas de frete) valem para
// todos os clientes: nos produtos do cliente a tela é só consulta e a edição fica
// no Everlog Admin › Cadastros globais (sprint 638; o backend nega a escrita a
// quem não é admin Everlog).
export const TEXTO_SOMENTE_CONSULTA =
  "Cadastro mantido pela Everlog; para incluir ou alterar, fale com o suporte.";

export const STATUS_ALL = "__ALL__";

export const STATUS_OPTIONS = [
  { value: STATUS_ALL, label: "Todos" },
  { value: "active", label: "Ativos" },
  { value: "inactive", label: "Inativos" },
];

export function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

export function isActive(status) {
  if (typeof status === "boolean") return status;
  return !["0", "false", "inativo", "nao"].includes(normalizeText(status));
}

export function matchesStatus(status, filter) {
  if (filter === "active") return isActive(status);
  if (filter === "inactive") return !isActive(status);
  return true;
}

export function apiErrorMessage(error, fallback) {
  return (error && error.response && error.response.data && error.response.data.error) || fallback;
}

export function toastError(addToast, title, error, fallback) {
  addToast({
    place: "tr",
    type: "danger",
    title,
    description: apiErrorMessage(error, fallback),
    icon: "tim-icons icon-alert-circle-exc",
  });
}

export function toastSuccess(addToast, title, description) {
  addToast({
    place: "tr",
    type: "success",
    title,
    description,
    icon: "tim-icons icon-check-2",
  });
}

const stateBoxStyle = {
  textAlign: "center",
  padding: "2.5rem 1rem",
  color: AUTOPILOT_COLORS.muted,
};

const stateTitleStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "0.98rem",
  fontWeight: 700,
  marginBottom: "0.35rem",
};

export const mutedStyle = { color: AUTOPILOT_COLORS.muted };

/**
 * Estado da lista: carregando, erro (com "Tentar novamente") ou vazio. Devolve
 * null quando há linhas para mostrar.
 */
export function ListState({ loading, error, empty, loadingText, emptyTitle, emptyText, onRetry }) {
  if (loading) {
    return (
      <div style={stateBoxStyle} data-testid="lista-carregando">
        <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent, marginRight: "0.5rem" }} />
        {loadingText}
      </div>
    );
  }
  if (error) {
    return (
      <div style={stateBoxStyle} data-testid="lista-erro">
        <div style={stateTitleStyle}>Não foi possível carregar</div>
        <p style={{ marginBottom: "0.75rem" }}>{error}</p>
        {onRetry ? (
          <Button size="sm" color="info" outline onClick={onRetry}>
            Tentar novamente
          </Button>
        ) : null}
      </div>
    );
  }
  if (empty) {
    return (
      <div style={stateBoxStyle} data-testid="lista-vazia">
        <div style={stateTitleStyle}>{emptyTitle}</div>
        <p style={{ marginBottom: 0 }}>{emptyText}</p>
      </div>
    );
  }
  return null;
}

/** Selo Ativo/Inativo (e variações de texto) com as cores de status do tema. */
export function StatusPill({ active, activeLabel = "Ativo", inactiveLabel = "Inativo" }) {
  return (
    <span className={`cg-pill ${active ? "cg-pill--success" : "cg-pill--neutral"}`}>
      {active ? activeLabel : inactiveLabel}
    </span>
  );
}

/** Selo genérico por tom (info, success, warning, danger, neutral). */
export function Pill({ tone = "neutral", children, title }) {
  return (
    <span className={`cg-pill cg-pill--${tone}`} title={title}>
      {children}
    </span>
  );
}

/** Confirmação antes de excluir/inativar, no padrão dos modais do Hub. */
export function ConfirmModal({
  isOpen,
  title,
  children,
  confirmLabel = "Confirmar",
  busyLabel = "Aguarde...",
  confirmColor = "danger",
  busy = false,
  onConfirm,
  onCancel,
}) {
  const cancel = () => (busy ? null : onCancel());
  return (
    <Modal isOpen={isOpen} toggle={cancel} modalClassName="modal-align-top" fade={false} size="sm">
      <ModalHeader toggle={cancel} tag="h5">
        {title}
      </ModalHeader>
      <ModalBody style={{ padding: "1.25rem 1.5rem" }}>
        <div style={{ marginBottom: "1.25rem" }}>{children}</div>
        <div className="cg-modal-footer">
          <Button color="default" outline size="sm" onClick={cancel} disabled={busy}>
            Cancelar
          </Button>
          <Button color={confirmColor} size="sm" onClick={onConfirm} disabled={busy} data-testid="confirmar">
            {busy ? busyLabel : confirmLabel}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
}
